import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Facebook, Instagram, Youtube, Twitter } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function subscribe(e) {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Look for our first note soon.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  }

  return (
    <footer data-testid="site-footer" className="bg-indigo text-parchment relative overflow-hidden">
      <span aria-hidden className="ghost-akshara left-[-6vw] bottom-[-14vh]" style={{ color: "#C98A2B", opacity: 0.05 }}>ಅ</span>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 relative z-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="min-w-0">
          <div className="flex items-center">
            <img src="/images/akshara-logo.png" alt="Akshara Daana" className="h-20 md:h-24 w-auto object-contain brightness-0 invert" />
          </div>
          <p className="mt-4 font-display text-sm text-white/70">
            A patient, transparent rural education movement grounded in Karnataka — the current chapter
            of a lifetime of work by Karnataka Kalashree Smt. Revathi Kamath.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span data-testid="tax-80g" className="px-3 py-1 bg-turmeric text-indigo text-[10px] uppercase font-display font-bold tracking-widest rounded-sm">80G · India</span>
            <span data-testid="tax-501c3" className="px-3 py-1 bg-vermilion text-parchment text-[10px] uppercase font-display font-bold tracking-widest rounded-sm">501(c)(3) · USA</span>
            <span data-testid="csr-reg" className="px-3 py-1 bg-teal text-white text-[10px] uppercase font-display font-bold tracking-widest rounded-sm">CSR Reg No. CSR00119564</span>
          </div>
          <p data-testid="parent-foundation" className="mt-4 font-display text-xs text-white/60 leading-relaxed">
            Akshara Daana is a unit of <span className="text-parchment/90 font-semibold">Revathi Raghuram Charitable Foundation</span>.
          </p>
        </div>

        <div className="min-w-0">
          <div className="text-turmeric text-xs uppercase tracking-widest font-display font-bold mb-4">Quick Links</div>
          <ul className="space-y-2 font-display text-sm text-parchment/80">
            <li><Link to="/about" className="hover:text-turmeric">About</Link></li>
            <li><Link to="/about#founder" className="hover:text-turmeric">Founder</Link></li>
            <li><Link to="/impact" className="hover:text-turmeric">Impact</Link></li>
            <li><Link to="/impact#stories" className="hover:text-turmeric">Stories of Change</Link></li>
            <li><Link to="/get-involved" className="hover:text-turmeric">Get Involved</Link></li>
            <li><Link to="/contact" className="hover:text-turmeric">Contact</Link></li>
            <li><Link to="/admin" data-testid="footer-admin-link" className="hover:text-turmeric">Admin</Link></li>
          </ul>
        </div>

        <div className="min-w-0">
          <div className="text-turmeric text-xs uppercase tracking-widest font-display font-bold mb-4">Programs</div>
          <ul className="space-y-2 font-display text-sm text-parchment/80">
            <li><Link to="/causes#programs" className="hover:text-turmeric">Quality Teachers</Link></li>
            <li><Link to="/causes#programs" className="hover:text-turmeric">Digital Learning</Link></li>
            <li><Link to="/causes#works" className="hover:text-turmeric">School Improvement</Link></li>
            <li><Link to="/causes#gallery" className="hover:text-turmeric">Before &amp; After</Link></li>
            <li><Link to="/causes#gadag" className="hover:text-turmeric">Gadag Expansion</Link></li>
          </ul>
        </div>

        <div className="min-w-0">
          <div className="text-turmeric text-xs uppercase tracking-widest font-display font-bold mb-4">Newsletter</div>
          <p className="font-display text-sm text-parchment/80 mb-3">
            One thoughtful update a month. Never a bulk mail.
          </p>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex gap-2">
            <input
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-0 bg-indigo-deep border border-turmeric/30 focus:border-turmeric px-3 py-2 text-sm font-display text-parchment focus:outline-none"
            />
            <button data-testid="newsletter-submit" disabled={busy} className="btn-vermilion !py-2 !px-4 text-sm shrink-0">
              {busy ? "…" : "Join"}
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a href="#" aria-label="Facebook" className="text-parchment/70 hover:text-turmeric"><Facebook className="w-5 h-5" /></a>
            <a href="#" aria-label="Instagram" className="text-parchment/70 hover:text-turmeric"><Instagram className="w-5 h-5" /></a>
            <a href="#" aria-label="Twitter" className="text-parchment/70 hover:text-turmeric"><Twitter className="w-5 h-5" /></a>
            <a href="#" aria-label="YouTube" className="text-parchment/70 hover:text-turmeric"><Youtube className="w-5 h-5" /></a>
            <span className="ml-1 sm:ml-3 text-parchment/50 text-xs font-display flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> Bengaluru · Gadag
            </span>
          </div>
        </div>
      </div>
      <div className="border-t border-parchment/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-display text-parchment/60 text-center md:text-left">
          <div className="text-center md:text-left">© {new Date().getFullYear()} Akshara Daana · A unit of Revathi Raghuram Charitable Foundation · CSR Reg No. CSR00119564</div>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-turmeric">Privacy</a>
            <a href="#" className="hover:text-turmeric">Financials</a>
            <a href="#" className="hover:text-turmeric">Annual Report</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
